import logger from '../utils/logger-rum';
import { buildSyncUrl, getSyncHeaders, getAuthenticatedRequestInit } from './utils';
import { getBullmanImageName } from '../utils/broadcastingImage';
import { HttpRequest } from '../stores/types/types';
import { Entity } from '../types/interfaces';
import { BroadcastEncodingOptions, Destination } from '../stores/types/broadcasting';

export function GetBroadcastServer(token: string, meeting: number) {
	const bullmanImageName = getBullmanImageName();
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/check/${meeting}`, {
		method: 'put',
		body: JSON.stringify({ bullmanImageName }),
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.json();
		}
		throw new Error(`COULD NOT GET ${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/server/${meeting} server status: ${res.status}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function StartBroadcast(
	token: string,
	meeting: number,
	resolution: string,
	broadcast: boolean = true,
	record: boolean = false,
) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/start/${meeting}`, {
		method: 'PUT',
		body: JSON.stringify({ resolution, broadcast, record }),
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.json();
		}
		throw new Error(`COULD NOT GET ${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/start/${meeting} server status: ${res.status}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function StopBroadcast(token: string, meeting: number) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/stop/${meeting}`, {
		method: 'PUT',
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.ok;
		}
		throw new Error(`COULD NOT GET ${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/stop/${meeting} server status: ${res.status}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function ManuallyCreateBroadcastServer(token: string, meeting: number) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/internal/broadcast/${meeting}`, {
		method: 'PUT',
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.ok;
		}
		throw new Error(`COULD NOT GET ${process.env.REACT_APP_API_HOST}/v2/greenroom/internal/broadcast/${meeting} server status: ${res.status}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function AutoStartBroadcast(token: string, meeting: number) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/auto-start/${meeting}`, {
		method: 'PUT',
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.ok;
		}
		throw new Error(`COULD NOT GET ${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/auto-start/${meeting} server status: ${res.status}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function SetDatabaseStreamState(token: string, meeting: number, streamState: number) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/stream-state`, {
		method: 'PUT',
		body: JSON.stringify({
			stream_state: streamState,
			meeting,
		}),
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.ok;
		}
		throw new Error(`Could not set stream state for meeting ${meeting}`);
	}).catch((error) => {
		console.error(error);
	});
}

export function GetDatabaseStreamState(token: string, meeting: number) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/stream-state/${meeting}`, {
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.json();
		}
		throw new Error(`Could not set stream state for meeting ${meeting}`);
	}).catch((error) => {
		console.error(error);
	});
}

export async function GetBroadcastingErrorLogs(meeting: number) {
	const logURL = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/error-logs/${meeting}`, {
		method: 'GET',
		...getAuthenticatedRequestInit(''),
	}).then((res) => {
		if (res.ok) {
			return res.json();
		}
		throw new Error(`Could not get error logs for meeting ${meeting}`);
	}).catch((error) => {
		console.error(error);
	});

	if (logURL && logURL.logUrl) {
		console.log(`BROADCASING ERROR. Full log available at ${logURL.logUrl}`);
	}
}

export function ResetBroadcastServer(token: string, meeting: number) {
	const bullmanImageName = getBullmanImageName();
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/broadcast/reset-broadcast-state/${meeting}`, {
		method: 'POST',
		body: JSON.stringify({ bullmanImageName }),
		...getAuthenticatedRequestInit(token),
	}).then((res) => {
		if (res.ok) {
			return res.json();
		}
		throw new Error('COULD NOT RESET BROADCAST');
	}).catch((error) => {
		console.error(error);
	});
}

export function isValidateRTMPEndpoint(streamAddress: string, streamKey: string) {
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/validate/rtmp-destination`, {
		method: 'POST',
		body: JSON.stringify({
			streamAddress,
			streamKey,
		}),
		...getAuthenticatedRequestInit(''),
	}).then((res) => {
		if (res.ok) {
			return true;
		}

		return false;
	}).catch((error) => {
		console.error(error);

		return false;
	});
}

export const StartSpielbergBroadcast = async (
	token: string,
	meetingId: number,
	broadcasting: boolean = true,
	recording: boolean = true,
	encodingOptionOverrides: BroadcastEncodingOptions = {},
	errorCallback?: () => void,
): Promise<boolean> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/broadcast/start`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					broadcasting,
					recording,
					encodingOptionOverrides,
				}),
			},
		);

		if (response.ok) {
			return true;
		}

		// surface error modal for internal server error
		if (response.status === 500 && errorCallback) {
			errorCallback();
		}

		logger.error(`StartSpielbergBroadcast error: ${response.status}`);
	} catch (error) {
		logger.error(`StartSpielbergBroadcast error: ${error}`);
	}

	return false;
};

export const StopSpielbergBroadcast = async (
	token: string,
	meetingId: number,
): Promise<boolean> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/broadcast/stop`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return true;
		}

		logger.error(`StopSpielbergBroadcast error: ${response.status}`);
	} catch (error) {
		logger.error(`StopSpielbergBroadcast error: ${error}`);
	}

	return false;
};

export const GetSpielbergBroadcast = async (
	token: string,
	meetingId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/broadcast`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: null;
		}

		logger.error(`StartSpielbergBroadcast error: ${response.status}`);
	} catch (error) {
		logger.error(`StartSpielbergBroadcast error: ${error}`);
	}

	return null;
};

export const UpdateBroadcastResolution = async (
	token: string,
	meetingId: number,
	resolution: string,
): Promise<boolean> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/broadcast-resolution`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify({ resolution }),
			},
		);

		if (response.ok) {
			return true;
		}

		logger.error(`UpdateBroadcastResolution error: ${response.status}`);
	} catch (error) {
		logger.error(`UpdateBroadcastResolution error: ${error}`);
	}

	return false;
};

export const GetDestinationsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/destination`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: null;
		}

		logger.error(`GetDestinationsForMeeting error: ${response.status}`);
	} catch (error) {
		logger.error(`GetDestinationsForMeeting error: ${error}`);
	}

	return null;
};

export const AddDestination = async (
	token: string,
	meetingId: number,
	destination: Destination,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/destination`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({ destination }),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`AddDestination error: ${response.status}`);
	} catch (error) {
		logger.error(`AddDestination error: ${error}`);
	}

	return null;
};

export const UpdateDestination = async (
	token: string,
	meetingId: number,
	destination: Destination,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/destination`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify({ destination }),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`UpdateDestination error: ${response.status}`);
	} catch (error) {
		logger.error(`UpdateDestination error: ${error}`);
	}

	return null;
};

export const DeleteDestination = async (
	token: string,
	meetingId: number,
	destinationId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`v1/room/${meetingId}/destination/${destinationId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`DeleteDestination error: ${response.status}`);
	} catch (error) {
		logger.error(`DeleteDestination error: ${error}`);
	}

	return null;
};
